/**
 * @file ページの上の書き込み (まなぶ人 × 資料 × ページごとの線) の読み書きである (Storage 直 write)。
 *
 * @what: `families/{familyId}/study_page_ink/{learnerId}/{sourceId}/{page}.strokes.json` に、
 *   線の JSON を application/octet-stream で置く。読むときは無ければ null を返す。
 *   解き直すときに残した過去の回は、同じ場所に `{page}.r{n}.strokes.json` で置く (n は1から)。
 *   資料ごとに、書き込みのあるページの番号と、ページごとの過去の回の番号も引ける
 *   (置いてあるファイルの名前から作る)。
 * @why: 本に書き込みながら読む体験のために、ページごとの線を残す。メモの手書きと同じく
 *   client から Storage に直接置く (posture B の線引き。教材への書き込みは成果物の写真と同じ扱い)。
 *   書き込みは storage.rules の study_page_ink の member write が前提である。
 *   過去の回を別のファイルにするのは、いまの線の読み書きの経路を変えずに済ませるためである。
 * @failure: 読みの失敗のうち「無い」(object-not-found) は null、それ以外は throw。書きの失敗と
 *   一覧の失敗 (権限が無いときなど) も throw する (呼び出し側が文言表示)。
 */

import {
  deleteObject,
  getBytes,
  getMetadata,
  listAll,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { getFirebaseStorage } from "@/shared/firebase/client";
import { parseHandwriting, serializeHandwriting, type HandwritingDoc } from "../model/handwriting";

/** 1ページに残せる過去の回の上限である。 */
export const PAGE_INK_ATTEMPTS_MAX = 9;

function pageInkDir(familyId: string, learnerId: string, sourceId: string): string {
  return `families/${familyId}/study_page_ink/${learnerId}/${sourceId}`;
}

export function pageInkPath(
  familyId: string,
  learnerId: string,
  sourceId: string,
  page: number,
): string {
  return `${pageInkDir(familyId, learnerId, sourceId)}/${page}.strokes.json`;
}

/** 過去の回 (n 回目) の線のパスである。 */
export function pageInkAttemptPath(
  familyId: string,
  learnerId: string,
  sourceId: string,
  page: number,
  attempt: number,
): string {
  return `${pageInkDir(familyId, learnerId, sourceId)}/${page}.r${attempt}.strokes.json`;
}

/**
 * ファイルの名前をページ番号と回の番号に分ける。いまの線なら attempt は null。
 * 当てはまらない名前は null。
 */
export function parsePageInkFileName(name: string): { page: number; attempt: number | null } | null {
  const m = /^(\d+)(?:\.r(\d+))?\.strokes\.json$/.exec(name);
  if (!m) return null;
  return { page: Number(m[1]), attempt: m[2] === undefined ? null : Number(m[2]) };
}

function isNotFound(err: unknown): boolean {
  const code = (err as { code?: unknown })?.code;
  return code === "storage/object-not-found";
}

/** ページの線を読む。無ければ null。形が壊れていても null (書き直せる)。 */
export async function loadPageInk(path: string): Promise<HandwritingDoc | null> {
  try {
    const bytes = await getBytes(storageRef(getFirebaseStorage(), path));
    return parseHandwriting(new TextDecoder().decode(bytes));
  } catch (err) {
    if (isNotFound(err)) return null;
    throw err;
  }
}

/** ページの線を置く (線が無ければ消す)。 */
export async function savePageInk(path: string, doc: HandwritingDoc): Promise<void> {
  const storage = getFirebaseStorage();
  if (doc.strokes.length === 0) {
    await deletePageInk(path);
    return;
  }
  await uploadBytes(storageRef(storage, path), new Blob([serializeHandwriting(doc)]), {
    contentType: "application/octet-stream",
  });
}

/** 線のファイルを消す。無ければ何もしない。 */
export async function deletePageInk(path: string): Promise<void> {
  try {
    await deleteObject(storageRef(getFirebaseStorage(), path));
  } catch (err) {
    if (!isNotFound(err)) throw err;
  }
}

/** 線のファイルを置いた日時である。無ければ null。 */
export async function loadPageInkSavedAt(path: string): Promise<Date | null> {
  try {
    const meta = await getMetadata(storageRef(getFirebaseStorage(), path));
    const d = new Date(meta.timeCreated);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (err) {
    if (isNotFound(err)) return null;
    throw err;
  }
}

export interface PageInkEntries {
  /** いまの線があるページの番号 (昇順)。 */
  inked: number[];
  /** ページ番号 → 残してある過去の回の番号 (昇順)。無いページは載せない。 */
  attempts: Map<number, number[]>;
}

/**
 * この資料で、そのまなぶ人が書き込んだページと、ページごとの過去の回を返す。
 *
 * 置いてあるファイルの名前をそのまま番号にする。※なぜなら書き込みは線が無くなると消すので、
 * ファイルの有無がそのまま「書き込みがあるか」になるためである。
 */
export async function listPageInkEntries(
  familyId: string,
  learnerId: string,
  sourceId: string,
): Promise<PageInkEntries> {
  const listed = await listAll(storageRef(getFirebaseStorage(), pageInkDir(familyId, learnerId, sourceId)));
  const inked: number[] = [];
  const attempts = new Map<number, number[]>();
  for (const item of listed.items) {
    const parsed = parsePageInkFileName(item.name);
    if (!parsed) continue;
    if (parsed.attempt === null) {
      inked.push(parsed.page);
    } else {
      const list = attempts.get(parsed.page) ?? [];
      list.push(parsed.attempt);
      attempts.set(parsed.page, list);
    }
  }
  inked.sort((a, b) => a - b);
  for (const list of attempts.values()) list.sort((a, b) => a - b);
  return { inked, attempts };
}
